import { useTranslate, type Identifier } from "ra-core";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import type { PipelineStage } from "../../types";
import { CardCreatePanel } from "./CardCreatePanel";
import { CardDetailsPanel } from "./CardDetailsPanel";

export type CardSheetState =
  | { mode: "closed" }
  | { mode: "create"; stageId: Identifier }
  | { mode: "view"; cardId: Identifier };

export interface CardSheetProps {
  state: CardSheetState;
  onChange: (state: CardSheetState) => void;
  pipelineId: Identifier;
  stages: PipelineStage[];
}

/** Side panel to create a card or see and edit one, like the Jivo card view. */
export const CardSheet = ({
  state,
  onChange,
  pipelineId,
  stages,
}: CardSheetProps) => {
  const translate = useTranslate();
  const close = () => onChange({ mode: "closed" });

  return (
    <Sheet
      open={state.mode !== "closed"}
      onOpenChange={(open) => !open && close()}
    >
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader className="pb-0">
          <SheetTitle className="text-sm font-medium text-muted-foreground">
            {translate(
              state.mode === "create"
                ? "crm.operations.cards.create_title"
                : "crm.operations.title",
            )}
          </SheetTitle>
        </SheetHeader>
        <div className="px-4 pt-2">
          {state.mode === "create" && (
            <CardCreatePanel
              pipelineId={pipelineId}
              stageId={state.stageId}
              stages={stages}
              onCancel={close}
              onCreated={(card) => onChange({ mode: "view", cardId: card.id })}
            />
          )}
          {state.mode === "view" && (
            <CardDetailsPanel
              key={state.cardId}
              cardId={state.cardId}
              stages={stages}
              onRemoved={close}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
